import React from "react";
import { useTranslation } from "react-i18next";
import { DateTimeInput } from "./dateTimeInput";
import { DateTimeInput as UnavailabilityDatePicker } from "@common/dateTimeInput";
import { TimeInput } from "@common/timeInput";
import { Checkbox } from "@common/checkbox";
import { DateTime } from "luxon";
import { Props } from "./types";
import styles from "./styles.scss";

function DateFields({
  type,
  startDate,
  endDate,
  partialUnavailability,
  selectedProject,
  setStartDate,
  setEndDate,
  setPartialUnavailability,
}: Props) {
  const { t } = useTranslation();
  const projectTimezone =
    selectedProject?.timeZone || DateTime.local().zoneName;

  if (type === "unavailability") {
    return (
      <>
        <div className={styles.row}>
          <div
            className={styles.checkbox}
            onClick={() => setPartialUnavailability(false)}
          >
            <Checkbox isChecked={!partialUnavailability} />
            <label className={styles.dateLabel}>
              {t("unavailability.creation.fullDay")}
            </label>
          </div>
          <div
            className={styles.checkbox}
            onClick={() => setPartialUnavailability(true)}
          >
            <Checkbox isChecked={partialUnavailability} />
            <label className={styles.dateLabel}>
              {t("unavailability.creation.partialDay")}
            </label>
          </div>
        </div>

        {!partialUnavailability ? (
          <div className={styles.dateRow}>
            <div className={styles.dateField}>
              <label className={styles.dateLabel}>
                {t("calendar.creationMenu.start")}
              </label>
              <UnavailabilityDatePicker
                calendarPosition="TOP"
                dateTime={startDate}
                projectTimezone={projectTimezone}
                placeholder=""
                input={(date) => {
                  setStartDate(date);
                  if (endDate && endDate < date) setEndDate(date);
                }}
              />
            </div>
            <div className={styles.dateField}>
              <label className={styles.dateLabel}>
                {t("calendar.creationMenu.end")}
              </label>
              <UnavailabilityDatePicker
                calendarPosition="TOP"
                dateTime={endDate}
                projectTimezone={projectTimezone}
                placeholder=""
                input={setEndDate}
              />
            </div>
          </div>
        ) : (
          <div className={styles.partialGroup}>
            <div className={styles.dateField}>
              <label className={styles.dateLabel}>
                {t("unavailability.creation.durationDay")}
              </label>
              <UnavailabilityDatePicker
                calendarPosition="TOP"
                dateTime={startDate}
                projectTimezone={projectTimezone}
                placeholder=""
                input={(date) => {
                  const pickedDay = DateTime.fromISO(date, {
                    zone: projectTimezone,
                  });
                  if (!pickedDay.isValid) return;

                  const currentStart = startDate
                    ? DateTime.fromISO(startDate, { zone: "utc" }).setZone(
                        projectTimezone,
                      )
                    : null;
                  const currentEnd = endDate
                    ? DateTime.fromISO(endDate, { zone: "utc" }).setZone(
                        projectTimezone,
                      )
                    : null;

                  const startH = currentStart?.isValid ? currentStart.hour : 0;
                  const startM = currentStart?.isValid
                    ? currentStart.minute
                    : 0;
                  const endH = currentEnd?.isValid ? currentEnd.hour : 0;
                  const endM = currentEnd?.isValid ? currentEnd.minute : 0;

                  const newStartDt = DateTime.fromObject(
                    {
                      year: pickedDay.year,
                      month: pickedDay.month,
                      day: pickedDay.day,
                      hour: startH,
                      minute: startM,
                      second: 0,
                      millisecond: 0,
                    },
                    { zone: projectTimezone },
                  );
                  const newEndDt = endDate
                    ? DateTime.fromObject(
                        {
                          year: pickedDay.year,
                          month: pickedDay.month,
                          day: pickedDay.day,
                          hour: endH,
                          minute: endM,
                          second: 0,
                          millisecond: 0,
                        },
                        { zone: projectTimezone },
                      )
                    : null;

                  if (!newStartDt.isValid) return;

                  setStartDate(newStartDt.toUTC().toISO() ?? "");
                  setEndDate(
                    newEndDt && newEndDt.isValid
                      ? (newEndDt.toUTC().toISO() ?? "")
                      : "",
                  );
                }}
              />
            </div>
            <div className={styles.dateRow}>
              <div className={styles.dateField}>
                <label className={styles.dateLabel}>
                  {t("calendar.creationMenu.start")}
                </label>
                <TimeInput
                  position="top"
                  value={startDate}
                  input={setStartDate}
                  timezone={projectTimezone}
                />
              </div>
              <div className={styles.dateField}>
                <label className={styles.dateLabel}>
                  {t("calendar.creationMenu.end")}
                </label>
                <TimeInput
                  position="top"
                  value={endDate}
                  input={setEndDate}
                  timezone={projectTimezone}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.dateRow}>
      <div className={styles.dateField}>
        <label className={styles.dateLabel}>
          {t("calendar.creationMenu.start")}
        </label>
        <DateTimeInput
          dateTime={startDate}
          projectTimezone={
            selectedProject?.timeZone || DateTime.local().zoneName
          }
          placeholder=""
          input={(combined) => {
            if (combined === null) {
              return setStartDate("");
            }

            setStartDate(combined);
            const start = DateTime.fromISO(combined, { zone: "utc" });
            const end = DateTime.fromISO(endDate, { zone: "utc" });

            if (end <= start) {
              setEndDate(start.plus({ hours: 1 }).toUTC().toISO() || endDate);
            }
          }}
        />
      </div>
      {type !== "milestone" && (
        <div className={styles.dateField}>
          <label className={styles.dateLabel}>
            {t("calendar.creationMenu.end")}
          </label>
          <DateTimeInput
            dateTime={endDate}
            projectTimezone={
              selectedProject?.timeZone || DateTime.local().zoneName
            }
            placeholder=""
            input={(d) => setEndDate(d || endDate)}
          />
        </div>
      )}
    </div>
  );
}

export { DateFields };
